// Pure logic for the Player Stats query drawer (FE4). No React/DOM — this is the
// tested core: turn the drawer's settings + the section scope into a
// POST /player-stats body, and turn a result row + settings into an ordered set
// of table columns. The widget (StatsTableWidget) is just the disposable UI over
// these functions.

/** The per-widget drawer settings. Held session-only in the canvas store, keyed
 *  by the widget's instanceId (see store/canvas.ts). `positionId`/`teamId` are
 *  the two whitelisted filters; the exotic Catalyst filters (period, goal diff,
 *  players-on-pitch) are deferred (frontend-plan §5.4). */
export interface StatsConfig {
  keys: string[];
  groupBy: string[];
  per90: boolean;
  positionId: string | null;
  teamId: number | null;
}

/** Sensible starting query so a freshly-added widget shows something real. */
export const DEFAULT_STATS_CONFIG: StatsConfig = {
  keys: ["goals", "assists", "minutes", "passes"],
  groupBy: ["player_id"],
  per90: false,
  positionId: null,
  teamId: null,
};

/** The group-by dimensions the backend whitelists (validate.ts GROUP_BY_KEYS)
 *  MINUS the two that come from scope (season_id, competition_id). Order here is
 *  the checkbox order in the drawer. */
export const GROUP_BY_OPTIONS: { key: string; label: string }[] = [
  { key: "player_id", label: "Player" },
  { key: "position_id", label: "Position" },
  { key: "team_id", label: "Team" },
  { key: "team_formation", label: "Formation" },
  { key: "match_id", label: "Match" },
];

export interface StatsBody {
  keys: string[];
  where: Record<string, (string | number)[]>;
  groupBy: string[];
}

/**
 * Build the POST /player-stats body from scope + drawer settings.
 *
 * Invariants the backend enforces (validate.ts) that we honour here:
 * - `competition_id` is present with EXACTLY ONE id (it comes from scope).
 * - `season_id` comes from scope too.
 * - position/team are added only when the user set them.
 * - `groupBy` never carries season_id/competition_id (they're scope dims, and
 *   the backend excludes season_id from group-by) — filtered defensively.
 *
 * competitionId/seasonId are non-null by type: the calling hook stays disabled
 * until both are known, so we never build a body with a missing scope id.
 */
export function buildStatsBody(
  competitionId: number,
  seasonId: number,
  config: StatsConfig
): StatsBody {
  const where: Record<string, (string | number)[]> = {
    competition_id: [competitionId],
    season_id: [seasonId],
  };
  if (config.positionId !== null) where.position_id = [config.positionId];
  if (config.teamId !== null) where.team_id = [config.teamId];

  return {
    keys: config.keys,
    where,
    groupBy: config.groupBy.filter(
      (g) => g !== "season_id" && g !== "competition_id"
    ),
  };
}

/** One rendered table column. Dimension (group-by) columns come first, then the
 *  selected metric columns; metric columns are right-aligned numbers. */
export interface ColumnSpec {
  key: string; // the row property to read
  label: string;
  numeric: boolean;
}

// Nice labels for the dimension columns we can produce. `team_formation` has no
// id suffix and no enriched name, so it shows as-is.
const DIM_LABELS: Record<string, string> = {
  player: "Player",
  team: "Team",
  position: "Position",
  match: "Match",
  team_formation: "Formation",
};

function humanize(s: string): string {
  return s
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Ordered columns for the table: group-by/context columns first (in the user's
 * group-by order), then metric columns in the user's selected order.
 *
 * Group-by dims come back enriched with a `<base>_name` column (e.g. group by
 * `player_id` → the row carries `player_name`). We show that name column and
 * therefore NEVER show the raw `*_id` column — the id is hidden, the human name
 * takes its place. If a dim has no enriched name in the row (e.g. a match with
 * no label, or `team_formation`), we fall back to the group-by key itself so the
 * group is still identifiable.
 *
 * `metricLabels` (optional) maps a metric key → its short display name from the
 * metric catalogue; falls back to a humanized key so this stays pure/testable.
 */
export function orderedColumns(
  row: Record<string, unknown>,
  keys: string[],
  groupBy: string[],
  metricLabels: Record<string, string> = {}
): ColumnSpec[] {
  const cols: ColumnSpec[] = [];

  for (const g of groupBy) {
    const base = g.endsWith("_id") ? g.slice(0, -3) : g;
    const nameCol = `${base}_name`;
    if (nameCol in row) {
      cols.push({ key: nameCol, label: DIM_LABELS[base] ?? humanize(base), numeric: false });
    } else {
      cols.push({ key: g, label: DIM_LABELS[base] ?? humanize(g), numeric: false });
    }
  }

  for (const k of keys) {
    cols.push({ key: k, label: metricLabels[k] ?? humanize(k), numeric: true });
  }

  return cols;
}

export type SortDir = "asc" | "desc";
export interface SortState {
  key: string;
  dir: SortDir;
}

/**
 * The header-click sort cycle: off → asc → desc → off. A click on a different
 * column starts that column at asc.
 */
export function nextSort(current: SortState | null, key: string): SortState | null {
  if (!current || current.key !== key) return { key, dir: "asc" };
  if (current.dir === "asc") return { key, dir: "desc" };
  return null;
}

type Cell = number | string | null;

/**
 * Sort rows by a column. Numbers compare numerically, everything else by
 * locale string; null/undefined always sort last regardless of direction (an
 * absent value shouldn't jump to the top). Pure — returns a new array.
 */
export function sortRows<T extends Record<string, Cell | undefined>>(
  rows: T[],
  sort: SortState | null
): T[] {
  if (!sort) return rows;
  const { key, dir } = sort;
  const sign = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    const aEmpty = av === null || av === undefined;
    const bEmpty = bv === null || bv === undefined;
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    if (typeof av === "number" && typeof bv === "number") return (av - bv) * sign;
    return String(av).localeCompare(String(bv)) * sign;
  });
}

/** Count of active (set) filters — drives the "Configure (n)" badge. */
export function activeFilterCount(config: StatsConfig): number {
  return (config.positionId !== null ? 1 : 0) + (config.teamId !== null ? 1 : 0);
}

/**
 * Reconcile selected metric keys against the catalogue for a given per90 mode.
 *
 * The metric catalogue pairs each metric with a `<key>_90` variant and shows one
 * or the other depending on `per90`. So when the user flips per90, a selected key
 * (e.g. `goals`) may no longer be offered — its counterpart (`goals_90`) is. This
 * maps each selected key to whichever of {itself, its per90 counterpart} the new
 * catalogue actually offers, and drops keys with no valid form (keeps the
 * selection meaningful across a per90 toggle). Order preserved, de-duplicated.
 */
export function remapKeysForCatalogue(keys: string[], validKeys: Set<string>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const k of keys) {
    const mapped = validKeys.has(k)
      ? k
      : k.endsWith("_90")
        ? k.slice(0, -3)
        : `${k}_90`;
    if (validKeys.has(mapped) && !seen.has(mapped)) {
      seen.add(mapped);
      out.push(mapped);
    }
  }
  return out;
}
